import React from "react";
import "./EditThemePreferences.scss";
import ColorEditIcon from "../../../components/icons/ColorEditIcon";

const EditThemePreferences = ({
  formik,
  prefix,
  originalColors,
}: {
  formik: any;
  prefix?: string;
  originalColors?: any;
}) => {
  const normalizeColor = (color: any) => {
    if (!color) return null;

    if (color.rgb) {
      return {
        r: color.rgb.r,
        g: color.rgb.g,
        b: color.rgb.b,
        hex: color.hex,
      };
    }

    if (color.r !== undefined) {
      return {
        r: color.r,
        g: color.g,
        b: color.b,
        hex: color.hex,
      };
    }

    return null;
  };

  const rgbToHex = (r: any, g: any, b: any) => {
    const red = Number(r);
    const green = Number(g);
    const blue = Number(b);

    return (
      "#" +
      [red, green, blue]
        .map((x) => {
          const hex = x.toString(16);
          return hex.length === 1 ? "0" + hex : hex;
        })
        .join("")
    ).toUpperCase();
  };

  const hexToAgencyColor = (hex: string) => {
    return {
      hex,
      rgb: {
        r: parseInt(hex.slice(1, 3), 16),
        g: parseInt(hex.slice(3, 5), 16),
        b: parseInt(hex.slice(5, 7), 16),
      },
    };
  };

  const toFormikColor = (color: any) => {
    if (!color) return null;

    if (typeof color === "string" && color.startsWith("#")) {
      return hexToAgencyColor(color);
    }

    if (color?.hex && color?.rgb?.r != null) {
      return { hex: color.hex, rgb: { ...color.rgb } };
    }

    if (
      color?.hex &&
      color?.r != null &&
      color?.g != null &&
      color?.b != null
    ) {
      return { hex: color.hex, rgb: { r: color.r, g: color.g, b: color.b } };
    }

    if (color?.hex) {
      return hexToAgencyColor(color.hex);
    }

    return null;
  };

const handleResetColors = () => {
  if (!originalColors) {
    console.warn("⚠️ No original colors available to reset");
    return;
  }

  const safeSet = (fieldName: string, value: any) => {
    const converted = toFormikColor(value);
    if (converted) {
      formik.setFieldValue(fieldName, converted);
    } else {
      console.warn(
        `⚠️ Skipping ${fieldName} reset — no original value in agency record`,
      );
    }
  };

  safeSet(`${prefix}_base_color_primary`, originalColors.base_color_primary);
  safeSet(`${prefix}_base_color_secondary`, originalColors.base_color_secondary);
  safeSet(`${prefix}_text_color_primary`, originalColors.text_color_primary);
  safeSet(`${prefix}_text_color_secondary`, originalColors.text_color_secondary);
};

  const basePrimary = normalizeColor(
    formik.values?.[`${prefix}_base_color_primary`],
  );

  const baseSecondary = normalizeColor(
    formik.values?.[`${prefix}_base_color_secondary`],
  );

  const textPrimary = normalizeColor(
    formik.values?.[`${prefix}_text_color_primary`],
  );

  const textSecondary = normalizeColor(
    formik.values?.[`${prefix}_text_color_secondary`],
  );

  return (
    <div className="design-theme-preferences-container">
      <div className="agency-color-grid">
        <div className="agency-color-contain">
          <p className="color-lable-head">Base Color Primary</p>

          <div className="edit-btn-and-text-contain">
            <p className="lable">
              {basePrimary
                ? rgbToHex(basePrimary.r, basePrimary.g, basePrimary.b)
                : ""}
            </p>

            <div className="color-edit-btn">
              <input
                type="color"
                className="color-input-contain"
                value={
                  basePrimary
                    ? rgbToHex(basePrimary.r, basePrimary.g, basePrimary.b)
                    : "#000000"
                }
                onChange={(e) =>
                  formik.setFieldValue(
                    `${prefix}_base_color_primary`,
                    hexToAgencyColor(e.target.value),
                  )
                }
              />
              <ColorEditIcon />
            </div>
          </div>

          <div
            className="color-bg"
            style={{
              backgroundColor: basePrimary
                ? rgbToHex(basePrimary.r, basePrimary.g, basePrimary.b)
                : "",
              color: textPrimary
                ? rgbToHex(textPrimary.r, textPrimary.g, textPrimary.b)
                : "",
            }}
          >
            <h4>Text Color Primary</h4>
            <p>Text preview with Text Color Primary</p>
          </div>
        </div>

        <div className="agency-color-contain">
          <p className="color-lable-head">Base Color Secondary</p>

          <div className="edit-btn-and-text-contain">
            <p className="lable">
              {baseSecondary
                ? rgbToHex(baseSecondary.r, baseSecondary.g, baseSecondary.b)
                : ""}
            </p>

            <div className="color-edit-btn">
              <input
                type="color"
                className="color-input-contain"
                value={
                  baseSecondary
                    ? rgbToHex(
                        baseSecondary.r,
                        baseSecondary.g,
                        baseSecondary.b,
                      )
                    : "#000000"
                }
                onChange={(e) =>
                  formik.setFieldValue(
                    `${prefix}_base_color_secondary`,
                    hexToAgencyColor(e.target.value),
                  )
                }
              />
              <ColorEditIcon />
            </div>
          </div>

          <div
            className="color-bg"
            style={{
              backgroundColor: baseSecondary
                ? rgbToHex(baseSecondary.r, baseSecondary.g, baseSecondary.b)
                : "",
              color: textSecondary
                ? rgbToHex(textSecondary.r, textSecondary.g, textSecondary.b)
                : "",
            }}
          >
            <h4>Text Color Secondary</h4>
            <p>Text preview with Text Color Secondary</p>
          </div>
        </div>

        <div className="agency-color-contain">
          <p className="color-lable-head">Text Color Primary</p>

          <div className="edit-btn-and-text-contain">
            <p className="lable">
              {textPrimary
                ? rgbToHex(textPrimary.r, textPrimary.g, textPrimary.b)
                : ""}
            </p>

            <div className="color-edit-btn">
              <input
                type="color"
                className="color-input-contain"
                value={
                  textPrimary
                    ? rgbToHex(textPrimary.r, textPrimary.g, textPrimary.b)
                    : "#000000"
                }
                onChange={(e) =>
                  formik.setFieldValue(
                    `${prefix}_text_color_primary`,
                    hexToAgencyColor(e.target.value),
                  )
                }
              />
              <ColorEditIcon />
            </div>
          </div>

          <div
            className="color-bg"
            style={{
              backgroundColor: textPrimary
                ? rgbToHex(textPrimary.r, textPrimary.g, textPrimary.b)
                : "",
            }}
          />
        </div>

        <div className="agency-color-contain">
          <p className="color-lable-head">Text Color Secondary</p>

          <div className="edit-btn-and-text-contain">
            <p className="lable">
              {textSecondary
                ? rgbToHex(textSecondary.r, textSecondary.g, textSecondary.b)
                : ""}
            </p>

            <div className="color-edit-btn">
              <input
                type="color"
                className="color-input-contain"
                value={
                  textSecondary
                    ? rgbToHex(
                        textSecondary.r,
                        textSecondary.g,
                        textSecondary.b,
                      )
                    : "#000000"
                }
                onChange={(e) =>
                  formik.setFieldValue(
                    `${prefix}_text_color_secondary`,
                    hexToAgencyColor(e.target.value),
                  )
                }
              />
              <ColorEditIcon />
            </div>
          </div>

          <div
            className="color-bg"
            style={{
              backgroundColor: textSecondary
                ? rgbToHex(textSecondary.r, textSecondary.g, textSecondary.b)
                : "",
            }}
          />
        </div>
      </div>

      <button
        type="button"
        className="reset-colors-btn"
        onClick={handleResetColors}
      >
        Reset colors
      </button>
    </div>
  );
};

export default EditThemePreferences;
